<template>
  <div class="content">
    <div v-if="!report.is_settled" class="no-print">
      <h2 class="content-heading border-bottom mb-4 pb-1">{{ $t('title_settle_report') }}</h2>
      <div class="row">
        <!-- Income Account Select -->
        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_income_account') }}<span style="color: red">*</span>
          </label>
          <Select v-model="incomeAccountId" :loading="accountsLoading" :options="incomeAccounts" optionLabel="title"
            optionValue="_id" :filter="true" :placeholder="$t('placeholder.select_account')" class="w-full">
            <template #option="slotProps">
              <span class="badge p-2 flex items-center gap-2" :class="'bg-' + (slotProps.option.type?.color || 'gray')">
                {{ slotProps.option.title }} - {{ slotProps.option.type?.title_ar || '' }}
              </span>
            </template>
          </Select>
        </div>

        <!-- Courier Select (Fixed with only report user) -->
        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_courier') }}<span style="color: red">*</span>
          </label>
          <Select v-model="deliveryManId" :options="[report.debtor]" optionLabel="first_name" optionValue="_id"
            :filter="false" :disabled="true" class="w-full">
            <template #value="slotProps">
              <span v-if="slotProps.value">
                {{ report.debtor.first_name }} {{ report.debtor.last_name }}
              </span>
            </template>
          </Select>
        </div>

        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_cod') }}<span style="color: red">*</span>
          </label>
          <InputGroup>
            <InputGroupAddon>EGP</InputGroupAddon>
            <InputNumber v-model.number="report.total_shipments_cod" class="w-full" size="small" disabled required
              placeholder="Price" />
            <InputGroupAddon>.00</InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      <div class="row">
        <!-- Second Courier Select (Same as first) -->
        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_courier') }}<span style="color: red">*</span>
          </label>
          <Select v-model="deliveryManId" :options="[report.debtor]" optionLabel="first_name" optionValue="_id"
            :filter="false" :disabled="true" class="w-full">
            <template #value="slotProps">
              <span v-if="slotProps.value">
                {{ report.debtor.first_name }} {{ report.debtor.last_name }}
              </span>
            </template>
          </Select>
        </div>

        <!-- Expense Account Select -->
        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_expense_account') }}<span style="color: red">*</span>
          </label>
          <Select v-model="expenseAccountId" :loading="accountsLoading" :options="expenseAccounts" optionLabel="title"
            optionValue="_id" :filter="true" :placeholder="$t('placeholder.select_account')" class="w-full"
            :class="{ 'p-invalid': !expenseAccountId && accountsLoaded }">
            <template #option="slotProps">
              <span class="badge p-2 flex items-center gap-2" :class="'bg-' + (slotProps.option.type?.color || 'gray')">
                {{ slotProps.option.title }} - {{ slotProps.option.type?.title_ar || '' }}
              </span>
            </template>
          </Select>
        </div>

        <div class="mb-4 col">
          <label class="form-label">
            {{ $t('input_courier_commission') }}<span style="color: red">*</span>
          </label>
          <InputGroup>
            <InputGroupAddon>EGP</InputGroupAddon>
            <InputNumber v-model.number="courierCash" class="w-full" size="small" required placeholder="Price" />
            <InputGroupAddon>.00</InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      <div class="d-flex justify-content-end mb-4">
        <button type="button" @click="settleReport" class="btn btn-sm btn-success" :disabled="loading">
          <i class="fa fa-fw fa-thumbs-up me-1"></i> {{ loading ? 'Processing...' : $t('button_settle') }}
        </button>
      </div>
    </div>

    <div class="block-rounded block">
      <div
        class="block-header block-header-default d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
        <h3 class="block-title">{{ report.reference }}</h3>

        <div class="block-options d-flex justify-content-sm-end mt-sm-0 mt-2 flex-wrap gap-2">
          <span v-if="report.is_settled === true"
            class="fs-xs fw-semibold d-inline-block rounded-pill bg-success-light text-success px-3 py-2">
            Settled
          </span>
          <span v-else-if="report.is_settled === false"
            class="fs-xs fw-semibold d-inline-block rounded-pill bg-danger-light text-danger px-3 py-2">
            Rejected
          </span>
          <span v-else class="fs-xs fw-semibold d-inline-block rounded-pill bg-warning-light text-warning px-3 py-2">
            Pending
          </span>

          <a v-if="!report.is_settled" :href="`/delivery-report/map-view/${report._id}`"
            class="btn btn-alt-primary btn-sm">
            <i class="si si-map me-1"></i> {{ $t('button_gen_routes') }}
          </a>

          <button type="button" class="btn btn-alt-primary btn-sm" @click="printPage">
            <i class="si si-printer me-1"></i> {{ $t('button_print') }}
          </button>
        </div>
      </div>

      <div class="block-content" style="direction: ltr; text-align: left;">
        <div class="p-sm-0 p-xl-5">
          <div class="row mb-4 d-flex justify-content-between">
            <div class="col-6 fs-sm text-start">
              <p class="h3">{{ report.debtor?.first_name }} {{ report.debtor?.last_name }}</p>
              <address>
                {{ report.debtor?.role_title?.role_title }}<br>
                {{ report.debtor?.phone_number }}<br>
              </address>
            </div>
            <div class="col-6 fs-sm text-end">
              <p class="h3">{{ partner.title }}</p>
              <address>
                {{ report.creator?.first_name }} {{ report.creator?.last_name }},
                {{ report.creator?.role_title?.role_title }}<br>
                {{ report.creator?.phone_number }}<br>
              </address>
            </div>
          </div>
          <div class="table-responsive push">
            <table class="fs-sm table-bordered table">
              <thead>
                <tr class="text-center">
                  <th></th>
                  <th>{{ $t('table_reference') }}</th>
                  <th>{{ $t('table_qrcode') }}</th>
                  <th>{{ $t('table_receiver') }}</th>
                  <th>{{ $t('table_address') }}</th>
                  <th>{{ $t('table_mobile_number') }}</th>
                  <th>{{ $t('table_status') }}</th>
                  <th>{{ $t('table_cod') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(shipment, index) in shipments" :key="shipment._id" class="text-center">
                  <td>{{ index + 1 }}</td>
                  <td>{{ shipment.reference }}</td>
                  <td>
                    <qrcode-vue :value="getQrCodeValue(shipment)" :size="70" level="H" class="qr-code" />
                  </td>
                  <td class="text-center">{{ shipment.receiver_name }}</td>
                  <td>
                    <p class="fw-semibold mb-1">
                      {{ formatAddress(shipment) }}
                    </p>
                    <div class="text-muted">{{ shipment.receiver_address }}</div>
                  </td>
                  <td class="text-center">{{ formatPhoneNumbers(shipment.receiver_sim_number) }}</td>
                  <td class="text-end">{{ shipment.status_data?.title_ar }}</td>
                  <td class="text-end">{{ formatCurrency(shipment.grand_total, shipment.is_reversed) }}</td>
                </tr>
                <tr>
                  <td colspan="7" class="fw-bold text-uppercase bg-body-light text-end">
                    {{ $t('input_total') }}</td>
                  <td class="fw-bold bg-body-light text-end">{{ report.total_shipments_cod }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="fs-sm text-muted text-center">
            Thank you very much for doing business with us. We look forward to working with you again!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
export default {
  components: {
    QrcodeVue
  },

  props: {
    report: {
      type: Object,
      required: true,
      default: () => ({
        _id: '',
        reference: '',
        total_shipments_cod: 0,
        is_settled: null,
        user_id: '',
        debtor: {
          first_name: '',
          last_name: '',
          role: {},
          phone_number: ''
        },
        creator: {
          first_name: '',
          last_name: '',
          role: {},
          phone_number: ''
        }
      })
    },
    shipments: {
      type: Array,
      default: () => []
    },
    partner: {
      type: Object,
      default: () => { }
    }
  },

  data() {
    return {
      incomeAccounts: [],
      expenseAccounts: [],
      company: { title: '' },
      incomeAccountId: null, // Use null instead of empty string
      expenseAccountId: null, // Use null instead of empty string
      deliveryManId: this.report.user_id || null,
      courierCash: 0,
      showSettleModal: false,
      loading: false,
      accountsLoading: false,
      qrCanvases: []
    };
  },

  mounted() {
    this.deliveryManId = this.report.user_id;
    this.fetchAllAccounts();
  },

  methods: {
    getQrCodeValue(shipment) {
      return shipment.reference;
    },
    formatCurrency(value, isReversed = false) {
            if (value === null || value === undefined) return "-";

            const formattedValue = this.formatNumber(Math.abs(value));

            // Use parentheses ONLY if reversed (regardless of negative value)
            return isReversed
                ? `(${formattedValue})`
                : formattedValue;
        },

        // Your existing formatNumber (kept as-is)
        formatNumber(value) {
            return new Intl.NumberFormat().format(value);
        },

    showError(message) {
      Swal.fire({
        title: 'Error',
        text: message,
        icon: 'error'
      });
    },

    async settleReport() {
      console.log(this.incomeAccountId)
      if (!this.incomeAccountId || !this.expenseAccountId) {
        this.showError('Please select both Asset and Expense accounts.');
        return;
      }

      if (this.courierCash < 0) {
        this.showError('Delivery price must be greater than 0');
        return;
      }

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to settle this report?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, settle it!',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
        try {
          this.loading = true;
          await axios.post('/delivery-report/approve', {
            report_id: this.report._id,
            income_account_id: this.incomeAccountId,
            expense_account_id: this.expenseAccountId,
            courier_cash: parseFloat(this.courierCash)
          });

          Swal.fire({
            title: 'Settled!',
            text: 'The report has been settled successfully.',
            icon: 'success',
          }).then(() => {
          });
        } catch (error) {
          console.error('Error settling the report:', error);
          this.showError(error.response?.data?.message || 'An error occurred while settling the report.');
        } finally {
          this.loading = false;
        }
      }
    },

    async confirmSettlement() {
      await this.settleReport();
      this.showSettleModal = false;
    },

    printPage() {
      window.print();
    },

    generateQrCode(reference) {
      return `/api/qrcode?text=${encodeURIComponent(reference)}`;
    },

    formatAddress(shipment) {
      const city = shipment.AreaData?.city_name_ar || '';
      const governorate = shipment.AreaData?.governorate?.governorate_name_ar || '';
      return [city, governorate].filter(Boolean).join(', ');
    },

    async fetchAccounts(type_id) {
      try {
        const response = await axios.post('/accounts/get-accounts-of-type', {
          type_id: type_id
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching accounts:', error);
        this.accountsError = 'Failed to load accounts';
        throw error;
      }
    },

    async fetchAllAccounts() {
      try {
        this.accountsLoading = true;
        this.accountsError = null;

        const [incomeAccounts, expenseAccounts] = await Promise.all([
          this.fetchAccounts('4'), // Income accounts
          this.fetchAccounts('5')  // Expense accounts
        ]);

        // Assign the fetched data to component state
        this.incomeAccounts = incomeAccounts || [];
        this.expenseAccounts = expenseAccounts || [];

      } catch (error) {
        console.error('Error fetching all accounts:', error);
        this.accountsError = 'Failed to load account data';
      } finally {
        this.accountsLoading = false;
      }
    },

    formatPhoneNumbers(numbers) {
      if (!numbers) return '';
      return Array.isArray(numbers) ? numbers.join('\n') : numbers;
    },
  }
};
</script>

<style scoped>
/* Hide during printing */
@media print {
  .no-print {
    display: none !important;
  }

  /* Optional: Improve print layout for the remaining content */
  .block-rounded,
  .block-content {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>